import { useCallback, useEffect, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { colors } from '../../styles/theme';
import { getApiErrorMessage } from '../../lib/apiError';
import commitmentsService, { type WithdrawalPenalty } from '../../services/commitments.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import type { Cadence, CommitmentConfig, PublishConfigBody } from '../../types/commitments';
import {
  countNeedingAttention,
  formatAmount,
  formatRetentionPct,
  productConfigStatus,
  type ConfigStatusKey,
} from './commitmentConfigStatus.helpers';

const ALL_CADENCES: Cadence[] = ['one_time', 'weekly', 'monthly'];

const DEFAULT_BODY = `By signing this contract, the Member ({{client_name}}) commits to {{cadence_label}} into {{product_name}} for the amount of {{amount_text}} ({{frequency_label}}), starting {{start_date}}.

Either party may pause or cancel this commitment in writing. Missed deposits without prior notice may trigger a follow-up call from the assigned agent.

This document is governed by the laws applicable to QMI and is retained under our record-keeping policy.`;

interface FormState {
  product_id: string;
  allowed_cadences: Cadence[];
  min_amount: string;
  max_amount: string;
  default_day_of_month: string;
  default_day_of_week: string;
  grace_days: string;
  reminder_lead_days: string;
  /** Retention as a fraction string. "0.05" = 5%. */
  early_termination_retention_pct: string;
  contract_title: string;
  contract_body: string;
  notes: string;
}

function defaultForm(): FormState {
  return {
    product_id: '',
    allowed_cadences: ['monthly'],
    min_amount: '1.00',
    max_amount: '',
    default_day_of_month: '1',
    default_day_of_week: '',
    grace_days: '2',
    reminder_lead_days: '3',
    early_termination_retention_pct: '0.0500',
    contract_title: 'Deposit Commitment',
    contract_body: DEFAULT_BODY,
    notes: '',
  };
}

/** What stops this form from being published, in plain words; null when nothing does. The
 *  browser's own validation is off (`noValidate`) because its bubble never reached the screen. */
function formProblem(form: FormState): string | null {
  const min = Number.parseFloat(form.min_amount);
  if (Number.isNaN(min) || min <= 0) return 'The minimum amount must be greater than zero.';
  if (form.max_amount.trim()) {
    const max = Number.parseFloat(form.max_amount);
    if (Number.isNaN(max) || max <= 0) return 'The maximum amount must be greater than zero, or left blank.';
    if (max < min) return 'The maximum amount cannot be below the minimum.';
  }
  if (form.allowed_cadences.length === 0) return 'Choose at least one cadence.';
  const retention = Number.parseFloat(form.early_termination_retention_pct);
  if (!(retention >= 0 && retention < 1)) {
    return 'The early-termination retention must be a fraction from 0 up to, but not including, 1.';
  }
  return null;
}

export default function AdminCommitmentConfigs(): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  // Active config's retention per product — the panel's own read (the
  // product listings no longer carry it; audit #138).
  const [retentions, setRetentions] = useState<Record<string, string>>({});
  const [configs, setConfigs] = useState<CommitmentConfig[]>([]);
  // What funds will really charge. The config's own percentage is a local copy
  // that nothing keeps in step, so the two are shown side by side.
  const [penalty, setPenalty] = useState<WithdrawalPenalty | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const [list, active] = await Promise.all([
        portfolioService.listAllProducts(),
        commitmentsService.listActiveConfigRetentions(),
      ]);
      setProducts(list);
      setRetentions(
        Object.fromEntries(active.map((a) => [a.product_id, a.early_termination_retention_pct])),
      );
      if (list.length && !form.product_id) {
        setForm((f) => ({ ...f, product_id: list[0].id }));
      }
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load products'));
    }
  }, [form.product_id]);

  const loadConfigs = useCallback(async (pid: string) => {
    if (!pid) {
      setConfigs([]);
      setPenalty(null);
      return;
    }
    setLoading(true);
    try {
      const [rows, charge] = await Promise.all([
        commitmentsService.listProductConfigs(pid),
        commitmentsService.withdrawalPenalty(pid).catch(() => null),
      ]);
      setConfigs(rows);
      setPenalty(charge);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load configs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadConfigs(form.product_id);
  }, [form.product_id, loadConfigs]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErr(null);
    setFlash(null);
    const problem = formProblem(form);
    if (problem) {
      setErr(problem);
      return;
    }
    setSubmitting(true);
    try {
      const body: PublishConfigBody = {
        allowed_cadences: form.allowed_cadences,
        min_amount: form.min_amount,
        max_amount: form.max_amount.trim() ? form.max_amount : null,
        default_day_of_month: form.default_day_of_month
          ? Number.parseInt(form.default_day_of_month, 10)
          : null,
        default_day_of_week: form.default_day_of_week
          ? Number.parseInt(form.default_day_of_week, 10)
          : null,
        grace_days: Number.parseInt(form.grace_days, 10),
        reminder_lead_days: Number.parseInt(form.reminder_lead_days, 10),
        early_termination_retention_pct: form.early_termination_retention_pct,
        contract_title: form.contract_title,
        contract_body: form.contract_body,
        notes: form.notes || null,
      };
      await commitmentsService.publishProductConfig(form.product_id, body);
      setFlash('New version published');
      loadConfigs(form.product_id);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Publish failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCadence = (c: Cadence): void => {
    setForm((f) => ({
      ...f,
      allowed_cadences: f.allowed_cadences.includes(c)
        ? f.allowed_cadences.filter((x) => x !== c)
        : [...f.allowed_cadences, c],
    }));
  };

  let historyBody: JSX.Element;
  if (loading) {
    historyBody = <p>Loading…</p>;
  } else if (configs.length === 0) {
    historyBody = <p style={{ color: tokens.textMuted }}>No versions yet for this product.</p>;
  } else {
    historyBody = (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th>v</th>
            <th>Effective</th>
            <th>Cadences</th>
            <th>Range</th>
            <th>Grace / Reminder</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((c) => (
            <tr key={c.id}>
              <td>{c.version}</td>
              <td>
                {c.effective_from} → {c.effective_to ?? 'now'}
                {c.effective_to === null ? ' (active)' : ''}
              </td>
              <td>{c.allowed_cadences.join(', ')}</td>
              <td>
                {formatAmount(c.min_amount)} – {c.max_amount === null ? '∞' : formatAmount(c.max_amount)}
              </td>
              <td>
                {c.grace_days} / {c.reminder_lead_days}
              </td>
              <td>
                {formatRetentionPct(c.early_termination_retention_pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Product commitment configs"
        subtitle="Versioned business terms for deposit commitments per product"
      />

      {err ? <div style={errBoxStyle}>{err}</div> : null}
      {flash ? <div style={successBoxStyle}>{flash}</div> : null}

      {form.product_id && penalty && (
        <WithdrawalPenaltyPanel
          penalty={penalty}
          portalPct={configs.find((c) => c.effective_to === null)?.early_termination_retention_pct ?? null}
        />
      )}

      {products.length > 0 ? (
        <ProductConfigStatusPanel
          products={products}
          retentions={retentions}
          selectedId={form.product_id}
          onSelect={(id) => setForm((f) => ({ ...f, product_id: id }))}
        />
      ) : null}

      <section style={cardStyle}>
        <h2 style={h2Style}>Publish new version</h2>
        <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 12 }}>
          <label style={labelStyle}>
            Product
            {/* */}
            <select
              value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              style={inputStyle}
            >
              {[...products]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>

          <fieldset style={fsStyle}>
            <legend style={{ color: tokens.textMuted, fontSize: 12 }}>Allowed cadences</legend>
            {ALL_CADENCES.map((c) => (
              <label key={c} style={{ marginRight: 16 }}>
                <input
                  type="checkbox"
                  checked={form.allowed_cadences.includes(c)}
                  onChange={() => toggleCadence(c)}
                />{' '}
                {c}
              </label>
            ))}
          </fieldset>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Min amount
              {/* */}
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                min="0.01"
                value={form.min_amount}
                onChange={(e) => setForm({ ...form, min_amount: limitToCents(e.target.value) })}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Max amount (blank = unlimited)
              {/* */}
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                min="0.01"
                value={form.max_amount}
                onChange={(e) => setForm({ ...form, max_amount: limitToCents(e.target.value) })}
                style={inputStyle}
              />
            </label>
          </div>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Default day of month (monthly)
              {/* */}
              <input
                type="number"
                min="1"
                max="28"
                value={form.default_day_of_month}
                onChange={(e) => setForm({ ...form, default_day_of_month: e.target.value })}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Default day of week (weekly, 0=Mon)
              {/* */}
              <input
                type="number"
                min="0"
                max="6"
                value={form.default_day_of_week}
                onChange={(e) => setForm({ ...form, default_day_of_week: e.target.value })}
                style={inputStyle}
              />
            </label>
          </div>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Grace days (post-due before overdue)
              {/* */}
              <input
                type="number"
                min="0"
                max="30"
                value={form.grace_days}
                onChange={(e) => setForm({ ...form, grace_days: e.target.value })}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Reminder lead days
              {/* */}
              <input
                type="number"
                min="0"
                max="30"
                value={form.reminder_lead_days}
                onChange={(e) => setForm({ ...form, reminder_lead_days: e.target.value })}
                style={inputStyle}
              />
            </label>
          </div>

          <label style={labelStyle}>
            Early-termination retention (fraction, 0–1; &quot;0.05&quot; = 5%)
            {/* */}
            <input
              type="number"
              step="0.0001"
              min="0"
              max="0.9999"
              required
              value={form.early_termination_retention_pct}
              onChange={(e) =>
                setForm({ ...form, early_termination_retention_pct: e.target.value })
              }
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Contract title (PDF heading)
            {/* */}
            <input
              type="text"
              value={form.contract_title}
              onChange={(e) => setForm({ ...form, contract_title: e.target.value })}
              maxLength={255}
              required
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Contract body (placeholders: {'{{client_name}}'}, {'{{product_name}}'},{' '}
            {'{{cadence_label}}'}, {'{{amount_text}}'}, {'{{start_date}}'},{' '}
            {'{{frequency_label}}'}, {'{{currency}}'})
            <textarea
              value={form.contract_body}
              onChange={(e) => setForm({ ...form, contract_body: e.target.value })}
              maxLength={20000}
              rows={10}
              required
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }}
            />
          </label>

          <label style={labelStyle}>
            Notes (internal)
            {/* */}
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={1000}
              style={inputStyle}
            />
          </label>

          <button type="submit" disabled={submitting || !form.product_id} style={btnPrimary}>
            {submitting ? 'Publishing…' : 'Publish new version'}
          </button>
        </form>
      </section>

      <section style={{ ...cardStyle, marginTop: 24 }}>
        <h2 style={h2Style}>Version history</h2>
        {historyBody}
      </section>

      {form.product_id ? <VehicleTermsNote /> : null}
    </div>
  );
}

/** The management fee, early-withdrawal penalty, target yield, term and
 *  minimum investment used to be published here; they now live only in the
 *  funds console (0073) — the portal holds no vehicle configuration of its
 *  own and reads those terms live. */
function VehicleTermsNote(): JSX.Element {
  return (
    <section style={{ ...cardStyle, marginTop: 24 }}>
      <h2 style={h2Style}>Fees, yield, term & minimum</h2>
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        The management fee, early-withdrawal penalty, target yield, term and minimum
        investment are set per vehicle in the funds console — not here.
      </p>
    </section>
  );
}

// Badge palette per status. missing_config = red (can't be contracted),
// missing_penalty = amber (config exists, 0% penalty — advisory), configured
// = green. Amber reuses the gold accent since the token table has no warning.
const BADGE_TONE: Record<ConfigStatusKey, { bg: string; border: string; color: string }> = {
  missing_config: { bg: tokens.errorBg, border: tokens.errorBorder, color: tokens.errorText },
  missing_penalty: { bg: colors.goldLight, border: tokens.gold, color: tokens.navy },
  configured: { bg: tokens.successBg, border: tokens.successBorder, color: tokens.successText },
};

/** Overview of every active product's commitment-config status, so the
 *  super_admin sees at a glance which products are unconfigured or carry no
 *  early-exit penalty. Clicking a row selects it in the publish form below. */
function ProductConfigStatusPanel({
  products,
  retentions,
  selectedId,
  onSelect,
}: Readonly<{
  products: ProductSummary[];
  retentions: Record<string, string>;
  selectedId: string;
  onSelect: (id: string) => void;
}>): JSX.Element {
  const sorted = [...products].sort((a, b) => a.name.localeCompare(b.name));
  const needing = countNeedingAttention(
    products.map((p) => ({ early_termination_retention_pct: retentions[p.id] ?? null })),
  );
  // Both ternaries are pulled out of the JSX so neither nests inside the other
  // (Sonar S3358 counts a ternary nested in another's branch, even in a string).
  const productNoun = products.length === 1 ? 'product' : 'products';
  const summary =
    needing === 0
      ? 'All products have an early-exit penalty configured.'
      : `${needing} of ${products.length} ${productNoun} need attention.`;
  return (
    <section style={{ ...cardStyle, marginBottom: 24 }}>
      <h2 style={h2Style}>Configuration status</h2>
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 13,
          color: needing ? tokens.errorText : tokens.successText,
        }}
      >
        {summary}
      </p>
      <div style={{ display: 'grid', gap: 6 }}>
        {sorted.map((p) => {
          const status = productConfigStatus(retentions[p.id] ?? null);
          const tone = BADGE_TONE[status.key];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              style={{
                ...statusRowStyle,
                background: p.id === selectedId ? tokens.surfaceMuted : 'transparent',
              }}
            >
              <span style={{ fontSize: 13 }}>{p.name}</span>
              <span
                style={{
                  ...badgePillStyle,
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  color: tone.color,
                }}
              >
                {status.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const statusRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 12px',
  cursor: 'pointer',
  textAlign: 'left',
  color: tokens.textPrimary,
};
const badgePillStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 20,
};
const h2Style: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 16, color: tokens.textPrimary };
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
};
const fsStyle: React.CSSProperties = {
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 12px',
  color: tokens.textPrimary,
};
const twoColStyle: React.CSSProperties = {
  display: 'grid',
  // Two columns when there's room; collapses to one on narrow screens
  // (the inline-style equivalent of a responsive grid — no media query).
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: 12,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '10px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  color: tokens.textPrimary,
  fontSize: 13,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
const successBoxStyle: React.CSSProperties = {
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};

/**
 * What funds will actually charge a client who exits early, next to what this
 * screen's own config says.
 *
 * Since 0073 the vehicle's economics live in funds and the portal reads them
 * live; `early_termination_retention_pct` on a commitment config is a local
 * copy that nothing keeps in step. Showing only the local one meant a
 * divergence was invisible to the one person whose job is to configure the
 * product — so both are shown, and a disagreement is called out.
 */
function WithdrawalPenaltyPanel({ penalty, portalPct }: Readonly<{
  penalty: WithdrawalPenalty;
  portalPct: string | null;
}>): JSX.Element {
  const fundsFlat = penalty.flat_pct === null ? null : Number.parseFloat(penalty.flat_pct);
  const portal = portalPct === null ? null : Number.parseFloat(portalPct);
  const comparable = penalty.method === 'flat' && fundsFlat !== null && portal !== null;
  const diverges = comparable && Math.abs(fundsFlat - portal) > 0.000001;

  return (
    <div
      style={{
        border: `1px solid ${diverges ? colors.dangerBorder : colors.border}`,
        background: diverges ? colors.dangerBg : colors.surface,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: tokens.textPrimary, marginBottom: 6 }}>
        Surrender charge, as funds will apply it
      </div>

      {penalty.method === null && (
        <div style={{ fontSize: 13, color: colors.muted }}>
          This vehicle charges nothing for an early exit.
        </div>
      )}

      {penalty.method === 'flat' && fundsFlat !== null && (
        <div style={{ fontSize: 13, color: tokens.textPrimary }}>
          A flat {(fundsFlat * 100).toFixed(2)}% of the amount withdrawn
          {penalty.effective_from ? `, effective ${penalty.effective_from}` : ''}.
        </div>
      )}

      {penalty.method === 'tiered' && (
        <div style={{ fontSize: 13, color: tokens.textPrimary }}>
          Tiered by how long the money has been in:
          <ul style={{ margin: '6px 0 0 18px' }}>
            {penalty.tiers.map((t) => (
              <li key={t.up_to_month}>
                up to month {t.up_to_month}: {(Number.parseFloat(t.pct) * 100).toFixed(2)}%
              </li>
            ))}
          </ul>
        </div>
      )}

      {portal !== null && (
        <div style={{ fontSize: 12, color: colors.muted, marginTop: 8 }}>
          This screen’s active config says {(portal * 100).toFixed(2)}%.
          {diverges && (
            <strong style={{ color: colors.danger }}>
              {' '}They disagree — funds is what the client is actually charged.
            </strong>
          )}
          {!comparable && penalty.method !== null
            && ' The two cannot be compared directly while funds charges by tier.'}
        </div>
      )}
    </div>
  );
}
